import { apiClient } from '@/sdk/shared/apiClient';
import { getApiBaseUrl } from '@/lib/env';
import type {
  Wallet,
  DepositRequest,
  DepositRequestWithFile,
  DepositTransaction,
  CreateDepositResponse,
  DepositListResponse,
  WalletResponse,
  CompanyBankDetail,
  CompanyBankDetailsResponse,
  CryptoCurrency,
  CryptoCurrenciesResponse,
  DepositAsset,
  DepositAssetsResponse,
  InitiateUpiDepositPayload,
  InitiatedUpiDepositData,
  InitiateUpiDepositResponse,
  SubmitUpiProofPayload,
  InitiateBankDepositPayload,
  InitiatedBankDepositData,
  InitiateBankDepositResponse,
  SubmitBankProofPayload,
  UpiQrResponseData,
  UpiQrResponse,
  ActiveDepositData,
} from './types';

export const clientWalletApi = {
  // Client Wallet Operations
  getWalletBalance: async (clientId: string, tenantSlug?: string): Promise<Wallet> => {
    const params = tenantSlug ? `?tenantSlug=${tenantSlug}` : '';
    const response = await apiClient.get<WalletResponse>(`/wallet/${clientId}${params}`);
    return response.data;
  },

  // Deposit Operations
  createDepositRequest: async (data: DepositRequest): Promise<DepositTransaction> => {
    const response = await apiClient.post<CreateDepositResponse>('/deposit/request', data);
    return response.data;
  },

  createDepositRequestWithFile: async (
    data: DepositRequestWithFile
  ): Promise<DepositTransaction> => {
    const formData = new FormData();

    formData.append('tenantSlug', data.tenantSlug);
    formData.append('clientId', data.clientId);
    formData.append('amount', data.amount.toString());

    if (data.currency) {
      formData.append('currency', data.currency);
    }
    if (data.paymentMethod) {
      formData.append('paymentMethod', data.paymentMethod);
    }
    if (data.notes) {
      formData.append('notes', data.notes);
    }
    if (data.paymentProof) {
      formData.append('paymentProof', data.paymentProof);
    }

    const token = apiClient.getToken();

    const response = await fetch(
      `${getApiBaseUrl()}/deposit/request`,
      {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: formData,
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'An error occurred' }));
      throw {
        status: response.status,
        ...error,
      };
    }

    const result = await response.json();
    return result.data;
  },

  // Generate UPI QR code without initiating a deposit request
  getUpiQr: async (tenantSlug?: string, clientId?: string): Promise<UpiQrResponseData> => {
    const params = new URLSearchParams();
    if (tenantSlug) params.set('tenantSlug', tenantSlug);
    if (clientId) params.set('clientId', clientId);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<UpiQrResponse>(`/client/deposit/upi/qr${queryString}`);
    return (response as any).data || response;
  },

  // UPI Deposit Operations (Incomplete Lifecycle & Persistent Lock)
  initiateUpiDeposit: async (
    payload: InitiateUpiDepositPayload
  ): Promise<InitiatedUpiDepositData> => {
    const response = await apiClient.post<InitiateUpiDepositResponse>(
      '/client/deposit/upi/initiate',
      payload
    );
    return response.data;
  },

  submitUpiProof: async (
    payload: SubmitUpiProofPayload
  ): Promise<DepositTransaction> => {
    const formData = new FormData();
    formData.append('paymentProof', payload.paymentProof);
    if (payload.tenantSlug) formData.append('tenantSlug', payload.tenantSlug);
    if (payload.clientId) formData.append('clientId', payload.clientId);
    if (payload.reference) formData.append('reference', payload.reference);
    if (payload.notes) formData.append('notes', payload.notes);

    const token = apiClient.getToken();
    const response = await fetch(
      `${getApiBaseUrl()}/client/deposit/upi/${payload.transactionId}/submit-proof`,
      {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: formData,
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'An error occurred' }));
      throw {
        status: response.status,
        ...error,
      };
    }

    const result = await response.json();
    return result.data;
  },

  getActiveIncompleteUpiDeposit: async (
    tenantSlug: string,
    clientId: string
  ): Promise<InitiatedUpiDepositData | null> => {
    try {
      const response = await apiClient.get<{ success: boolean; data: InitiatedUpiDepositData | null }>(
        `/client/deposit/upi/active?tenantSlug=${tenantSlug}&clientId=${clientId}`
      );
      return response.data || null;
    } catch {
      return null;
    }
  },

  cancelUpiDeposit: async (
    transactionId: string,
    tenantSlug?: string,
    reason?: string
  ): Promise<any> => {
    const response = await apiClient.post<{ success: boolean; message: string; data: any }>(
      `/client/deposit/upi/${transactionId}/cancel`,
      { tenantSlug, reason }
    );
    return response.data;
  },


  // Bank Transfer Deposit Operations (Dynamic Range & Incomplete Lifecycle)
  initiateBankDeposit: async (
    payload: InitiateBankDepositPayload
  ): Promise<InitiatedBankDepositData> => {
    const response = await apiClient.post<InitiateBankDepositResponse>(
      '/client/deposit/bank-transfer/initiate',
      payload
    );
    return response.data;
  },

  submitBankProof: async (
    payload: SubmitBankProofPayload
  ): Promise<DepositTransaction> => {
    const formData = new FormData();
    formData.append('paymentProof', payload.paymentProof);
    if (payload.tenantSlug) formData.append('tenantSlug', payload.tenantSlug);
    if (payload.clientId) formData.append('clientId', payload.clientId);
    if (payload.reference) formData.append('reference', payload.reference);
    if (payload.notes) formData.append('notes', payload.notes);

    const token = apiClient.getToken();
    const response = await fetch(
      `${getApiBaseUrl()}/client/deposit/bank-transfer/${payload.transactionId}/submit-proof`,
      {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: formData,
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'An error occurred' }));
      throw {
        status: response.status,
        ...error,
      };
    }

    const result = await response.json();
    return result.data;
  },

  getActiveIncompleteBankDeposit: async (
    tenantSlug: string,
    clientId: string
  ): Promise<InitiatedBankDepositData | null> => {
    try {
      const response = await apiClient.get<{ success: boolean; data: InitiatedBankDepositData | null }>(
        `/client/deposit/bank-transfer/active?tenantSlug=${tenantSlug}&clientId=${clientId}`
      );
      return response.data || null;
    } catch {
      return null;
    }
  },

  cancelBankDeposit: async (
    transactionId: string,
    tenantSlug?: string,
    reason?: string
  ): Promise<any> => {
    const response = await apiClient.post<{ success: boolean; message: string; data: any }>(
      `/client/deposit/bank-transfer/${transactionId}/cancel`,
      { tenantSlug, reason }
    );
    return response.data;
  },

  // Active Incomplete Deposit (Across any payment method)
  getActiveDeposit: async (
    tenantSlug: string,
    clientId: string
  ): Promise<ActiveDepositData | null> => {
    try {
      const response = await apiClient.get<{ success: boolean; data: ActiveDepositData | null }>(
        `/client/deposit/active?tenantSlug=${encodeURIComponent(tenantSlug)}&clientId=${encodeURIComponent(clientId)}`
      );
      return response.data || null;
    } catch {
      return null;
    }
  },

  // Mark an incomplete deposit as expired
  markDepositExpired: async (
    transactionId: string,
    tenantSlug?: string
  ): Promise<any> => {
    const response = await apiClient.post<{ success: boolean; message: string; data: any }>(
      `/client/deposit/${transactionId}/expire`,
      { tenantSlug }
    );
    return response.data;
  },

  getClientDepositRequests: async (
    clientId: string,
    tenantSlug: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<DepositTransaction[]> => {
    const response = await apiClient.get<DepositListResponse>(
      `/deposit/client/${clientId}?tenantSlug=${tenantSlug}&limit=${limit}&offset=${offset}`
    );
    return response.data;
  },

  getRecentDepositRequests: async (
    tenantSlug: string,
    limit: number = 10
  ): Promise<DepositTransaction[]> => {
    const response = await apiClient.get<DepositListResponse>(
      `/client/deposit/recent?tenantSlug=${tenantSlug}&limit=${limit}`
    );
    return response.data;
  },

  // Company Bank Details
  getCompanyBankDetails: async (tenantSlug: string, clientId?: string): Promise<CompanyBankDetail[]> => {
    const params = new URLSearchParams();
    if (tenantSlug) params.set('tenantSlug', tenantSlug);
    if (clientId) params.set('clientId', clientId);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<CompanyBankDetailsResponse>(
      `/client/company-bank-details${queryString}`
    );
    // Map backend snake_case to camelCase
    return response.data.map((raw: any) => ({
      id: raw.id,
      bankName: raw.bank_name,
      accountNumber: raw.account_number,
      ifscCode: raw.ifsc_code,
      accountHolderName: raw.account_holder_name,
      accountType: raw.account_type,
      isPrimary: raw.is_primary,
      isFirstDeposit: raw.is_first_deposit ?? false,
      minAmount: raw.min_amount !== null && raw.min_amount !== undefined ? parseFloat(raw.min_amount) : null,
      maxAmount: raw.max_amount !== null && raw.max_amount !== undefined ? parseFloat(raw.max_amount) : null,
    }));
  },

  // Cryptocurrencies
  getCryptoCurrencies: async (tenantSlug: string): Promise<CryptoCurrency[]> => {
    const response = await apiClient.get<CryptoCurrenciesResponse>(
      `/client/crypto-currencies?tenantSlug=${tenantSlug}`
    );
    return response.data;
  },

  getCryptoImageUrl: (cryptoId: string, tenantSlug: string): string => {
    return `${getApiBaseUrl()}/client/crypto-currency/${cryptoId}/image?tenantSlug=${tenantSlug}`;
  },
  
  getActiveDepositAssets: async (environment: string = 'live'): Promise<DepositAsset[]> => {
    const response = await apiClient.get<DepositAssetsResponse>(
      `/admin/deposit-assets/active?environment=${environment}`
    );
    return response.data;
  },
};


export default clientWalletApi;
